// ExportTab.js
import React, { useEffect, useState } from 'react';
import { getToken } from '../../../utils/TokenUtils';
import { API_URL } from '../../../constants/api';
import jsPDF from 'jspdf'; // npm install jspdf jspdf-autotable
import 'jspdf-autotable';

export default function ExportTab({ table }) {
  const [columns, setColumns] = useState([]);
  const [data, setData] = useState([]);
  const [selectedCols, setSelectedCols] = useState([]);

  const token = getToken();

  useEffect(() => {
    try {
      fetch(`${API_URL}/api/sql/tables/${table}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
        .then((res) => res.json())
        .then((res) => {
          setColumns(res.columns);
          setData(res.rows);
          setSelectedCols(res.columns); // default: all selected
        });
    } catch (err) {
      alert(err);
    }
  }, [table]);

  // ✅ Toggle column selection
  const toggleColumn = (col) => {
    setSelectedCols((prev) =>
      prev.includes(col) ? prev.filter((c) => c !== col) : [...prev, col]
    );
  };

  const selectAll = () => setSelectedCols(columns);
  const clearAll = () => setSelectedCols([]);

  // ✅ Export CSV
  const exportCSV = () => {
    if (!data.length || !selectedCols.length) return;

    const csvRows = [];
    csvRows.push(selectedCols.join(',')); // Header

    for (const row of data) {
      const values = selectedCols.map(
        (col) => `"${(row[col] ?? '').toString().replace(/"/g, '""')}"`
      );
      csvRows.push(values.join(','));
    }

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${table}-export.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ✅ Export SQL
  const exportSQL = () => {
    if (!data.length || !selectedCols.length) return;

    const sqlStatements = data.map((row) => {
      const values = selectedCols
        .map((col) => {
          const val = row[col];
          if (val === null || val === undefined) return 'NULL';
          return `'${val.toString().replace(/'/g, "''")}'`;
        })
        .join(', ');
      return `INSERT INTO ${table} (${selectedCols.join(', ')}) VALUES (${values});`;
    });

    const blob = new Blob([sqlStatements.join('\n')], { type: 'text/sql' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${table}-export.sql`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ✅ Export PDF (Tabular)
  const exportPDF = () => {
    if (!data.length || !selectedCols.length) return;

    const doc = new jsPDF();
    doc.setFontSize(14);
    doc.text(`Exported Data from ${table}`, 14, 15);

    const tableData = data.map((row) =>
      selectedCols.map((col) => (row[col] ?? '').toString())
    );

    doc.autoTable({
      head: [selectedCols],
      body: tableData,
      startY: 25,
      styles: { fontSize: 10, cellPadding: 3 },
      headStyles: { fillColor: [41, 128, 185] }, // nice blue header
      alternateRowStyles: { fillColor: [245, 245, 245] },
    });

    doc.save(`${table}-export.pdf`);
  };

  if (!columns.length) return <p>Loading export options...</p>;

  return (
    <div>
      <p>
        Export data from <strong>{table}</strong>.
      </p>

      {/* ✅ Column Selection */}
      <div className="mb-3">
        <p>
          <strong>Select columns to export:</strong>
        </p>
        <button
          className="btn btn-sm btn-outline-secondary m-1"
          onClick={selectAll}
        >
          Select All
        </button>
        <button
          className="btn btn-sm btn-outline-secondary m-1"
          onClick={clearAll}
        >
          Clear All
        </button>
        <div>
          {columns.map((col) => (
            <label key={col} className="me-3">
              <input
                type="checkbox"
                checked={selectedCols.includes(col)}
                onChange={() => toggleColumn(col)}
              />{' '}
              {col}
            </label>
          ))}
        </div>
      </div>

      {/* ✅ Export Buttons */}
      <button className="btn btn-success m-1" onClick={exportCSV}>
        Export to CSV
      </button>
      <button className="btn btn-primary m-1" onClick={exportSQL}>
        Export to SQL
      </button>
      <button className="btn btn-danger m-1" onClick={exportPDF}>
        Export to PDF
      </button>
    </div>
  );
}
